import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../../../components/ui';
import { Settings, Check, X, Search, CheckCircle2, UserX, UserCheck } from 'lucide-react';
import api from '../../../services/api';
import toast from 'react-hot-toast';

export const ManageVersionsView = () => {
  const [selectedVersion, setSelectedVersion] = useState('');
  const [loading, setLoading] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [fetching, setFetching] = useState(true);

  const fetchMasterData = async () => {
    setFetching(true);
    try {
      const res = await api.get('/inventory-master');
      if (res && res.data) {
        setPurchases(res.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch InventoryMaster:", err);
      toast.error("Failed to fetch Master data");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchMasterData();
  }, []);

  // Get unique versions from the data
  const uniqueVersions = useMemo(() => {
    const versions = new Set();
    purchases.forEach(p => {
      if (p.version && typeof p.version === 'string' && p.version.trim()) {
        versions.add(p.version.trim());
      }
    });
    return Array.from(versions).sort();
  }, [purchases]);

  // Determine status of selected version
  const versionStatus = useMemo(() => {
    if (!selectedVersion) return null;
    const items = purchases.filter(p => p.version === selectedVersion);
    if (items.length === 0) return null; // Version not in table

    const hasActive = items.some(i => i.status !== 'Inactive');
    const hasInactive = items.some(i => i.status === 'Inactive');

    if (hasActive && hasInactive) return 'Mixed';
    if (hasActive) return 'Active';
    return 'Inactive';
  }, [selectedVersion, purchases]);

  const handleToggle = async (newStatus) => {
    try {
      setLoading(true);
      await api.put('/inventory-master/version-status', {
        version: selectedVersion,
        status: newStatus
      });
      toast.success(`Successfully updated all ${selectedVersion} items to ${newStatus}`);
      fetchMasterData();
    } catch (error) {
      console.error("Failed to update version status:", error);
      toast.error(error.response?.data?.message || "Failed to update version status");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return <div className="p-8 text-center text-slate-500">Loading versions...</div>;
  }

  return (
    <div className="card p-6 border border-slate-100 shadow-sm bg-white rounded-xl">
      <div className="flex flex-col gap-0.5 z-10 relative mb-6">
        <span className="text-[14px] font-bold text-slate-800 font-serif tracking-widest uppercase flex items-center gap-2">
          <Settings size={18} />
          MANAGE VERSIONS
        </span>
        <span className="text-[11px] text-slate-500 font-semibold tracking-widest uppercase">
          Enable or disable specific versions of materials
        </span>
      </div>

      <div className="max-w-md space-y-6">
        <div className="space-y-4">
          <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest">
            Select Version
          </label>
          
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-slate-400" />
            </div>
            <input
              list="versions-list"
              type="text"
              value={selectedVersion}
              onChange={(e) => setSelectedVersion(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/50 focus:border-brand-blue transition-all"
              placeholder="Type or select a version..."
            />
            <datalist id="versions-list">
              {uniqueVersions.map(v => (
                <option key={v} value={v} />
              ))}
            </datalist>
          </div>

          {selectedVersion && selectedVersion.trim() !== '' && (
            <div className="mt-4 p-4 rounded-lg border border-slate-100 bg-slate-50 animate-in fade-in slide-in-from-top-2">
              {versionStatus === null ? (
                <div className="flex items-center gap-2 text-rose-500 text-[12px] font-bold">
                  <X size={16} />
                  <span>Version not found in your table</span>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-emerald-600 text-[12px] font-bold">
                    <CheckCircle2 size={16} />
                    <span>Version found!</span>
                  </div>
                  <div className="text-[11px] text-slate-500 font-medium">
                    Current Status: <strong className="text-slate-800 uppercase">{versionStatus}</strong>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
          {versionStatus !== null && (
            <div className="flex items-center gap-2">
              {(versionStatus === 'Inactive' || versionStatus === 'Mixed') && (
                <Button 
                  onClick={() => handleToggle('Active')} 
                  disabled={loading}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold"
                  leftIcon={<UserCheck size={16} />}
                >
                  Enable Version
                </Button>
              )}
              
              {(versionStatus === 'Active' || versionStatus === 'Mixed') && (
                <Button 
                  onClick={() => handleToggle('Inactive')} 
                  disabled={loading}
                  className="bg-rose-500 hover:bg-rose-600 text-white font-bold"
                  leftIcon={<UserX size={16} />}
                >
                  Disable Version
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
